// API client — thin wrappers around the backend HTTP surface.
//
// Track C frontend consumes Track B (stream-2) backend endpoints. Until
// Track B lands, the dev workflow either mocks or hits a stubbed endpoint.

export interface CurrentUser {
  user_id: string;
  email: string;
  display_name: string;
  // Defer-verification: a signup can use the app before confirming email.
  // `false` ⇒ the app shell shows the "verify your email" banner (T-0087a2).
  // Wire shape mirrors backend `MeResponse.email_verified` in
  // `src/presentation/web/types.rs`. Optional in the type so older cached
  // payloads deserialize; the banner only shows on an explicit `false`.
  email_verified?: boolean;
}

export interface MemberSpace {
  room_id: string;
  /** The space's NAME — the authoritative title (migration #31's `rooms.name`). The wire
   *  ALWAYS carries it (Rust `MemberSpaceResponse.name`); it is optional here only so a
   *  legacy/cached row without it still resolves via the `description` fallback. The list
   *  must prefer this over `description`, exactly as the desktop settings list does — T-0312. */
  name?: string;
  description: string;
  state: 'active' | 'archived' | 'revoked';
  last_message_at: string | null;
  unread_count: number;
}

export interface MemberSpaceDetail extends MemberSpace {
  members: SpaceMember[];
  created_at: string;
}

export interface SpaceMember {
  user_id: string;
  display_name: string;
  persona: string;
  trust_level: 'self_device' | 'cross_human_persistent' | 'cross_human_guest';
  joined_at: string;
  last_activity_at: string | null;
}

export interface Message {
  message_id: string;
  room_id: string;
  sender_user_id: string;
  sender_persona: string;
  content: string;
  parent_id: string | null;
  addressed_to: string | null;
  attachments: Attachment[];
  sent_at: string;
}

export interface Attachment {
  attachment_id: string;
  message_id: string;
  filename: string;
  content_type: string;
  byte_size: number;
}

export interface MessagePage {
  messages: Message[];
  next_cursor: string | null;
}

// Admin-namespace "space" — distinct from the V0 "room" (member-of) concept.
// Wire shape mirrors backend `SpaceResponse` in src/presentation/spaces.rs.
// See `SPACES_BACKEND_WIRE.md` for the full contract.
export type SpaceState = 'active' | 'sleeping' | 'pending-deletion';

export interface Space {
  space_id: string;
  namespace_uri: string;
  name: string;
  description: string | null;
  state: SpaceState;
  created_at: string;
  last_activity_at: string | null;
  scheduled_purge_at?: string;
}

export interface ListSpacesResponse {
  spaces: Space[];
}

export interface ApiError {
  status: number;
  message: string;
  path: string;
  // Machine-readable backend error code, lifted from a `{error:{code,message}}`
  // body (see `readWebError`). Present when the backend sends the structured
  // shape — notably `sudo_required` on a 403 step-up gate, which
  // `authErrors.isSudoRequired` keys on to open the SudoModal instead of leaking
  // the raw "POST /api/sudo then retry" copy. Optional/additive: byte-identical
  // for every existing throw site that doesn't set it.
  code?: string;
}

// --- Desktop loopback seam (#111) -------------------------------------------
//
// In the Tauri desktop webview the SPA is served from `tauri://localhost`, where
// a relative `fetch('/api/…')` resolves to `tauri://localhost/api/*` — nothing
// answers, so the Sign-in screen shows "We couldn't connect to Tizok". Tauri's
// `initialization_script` (desktop item E) injects, BEFORE the SPA boots, the
// atlasd loopback-proxy origin + the per-launch shared secret. Every backend
// call routes through `apiUrl()` / `apiFetch()` so it targets that proxy and
// carries the secret header.
//
// On the WEB build these globals are undefined → `API_ORIGIN = ''` (relative
// URLs, behavior identical to before) and NO secret header is added — zero web
// risk. Locked by `__tests__/api-loopback.test.ts`.
interface TizokLoopbackGlobals {
  __TIZOK_API_ORIGIN__?: string;
  __TIZOK_LOOPBACK_SECRET__?: string;
}

function loopbackGlobals(): TizokLoopbackGlobals {
  return typeof window === 'undefined'
    ? {}
    : (window as unknown as TizokLoopbackGlobals);
}

// The header the atlasd loopback proxy gates on — mirrors `LOOPBACK_SECRET_HEADER`
// in `src/bin/atlasd/proxy.rs`. Lowercase per HTTP convention.
const LOOPBACK_SECRET_HEADER = 'x-tizok-loopback-secret';

/**
 * Resolve a backend path to a full URL: the injected proxy origin on desktop, or
 * `''` (relative, unchanged) on web.
 */
export function apiUrl(path: string): string {
  return (loopbackGlobals().__TIZOK_API_ORIGIN__ ?? '') + path;
}

// Add the loopback secret header when injected (desktop); a no-op on web (returns
// the caller's headers untouched, so the request is byte-identical).
function withLoopbackSecret(headers?: HeadersInit): HeadersInit | undefined {
  const secret = loopbackGlobals().__TIZOK_LOOPBACK_SECRET__;
  if (!secret) return headers;
  const merged = new Headers(headers);
  merged.set(LOOPBACK_SECRET_HEADER, secret);
  return merged;
}

/**
 * The ONE chokepoint for backend `fetch`es: rewrites the URL (proxy origin) and
 * attaches the loopback secret on desktop; identical to `fetch(path, init)` on
 * web. Every backend call in the app goes through this (incl. the raw XHR upload
 * below, via {@link apiUrl}).
 *
 * FU-2 (#111 coherence): the injected {origin, secret} are baked at window
 * creation, so an atlasd restart — which rotates BOTH the loopback PORT and the
 * per-launch secret — orphans the open window. Two distinct failure shapes, both
 * recovered here by re-reading the republished handoff and retrying:
 *   - rotated SECRET, same port → the proxy answers with a 401 → re-read + retry
 *     (only when the secret actually changed, so a genuine auth 401 is unchanged);
 *   - rotated PORT → the baked origin points at a DEAD port, so the fetch REJECTS
 *     (connection refused), never a 401 → {@link recoverRotatedLoopback}. A real
 *     restart ALWAYS rotates the ephemeral `:0` port, so this is the dominant case
 *     (Wall-B: Start-swarm SIGTERM-restarts atlasd and used to strand the console
 *     on the sign-in screen).
 * Web (no injected secret) never enters either path — behavior is byte-identical.
 */
export async function apiFetch(path: string, init: RequestInit = {}): Promise<Response> {
  await adoptLoopbackConfig();
  const staleOrigin = loopbackGlobals().__TIZOK_API_ORIGIN__;
  const staleSecret = loopbackGlobals().__TIZOK_LOOPBACK_SECRET__;
  let res: Response;
  try {
    res = await fetch(apiUrl(path), { ...init, headers: withLoopbackSecret(init.headers) });
  } catch (err) {
    // A caller-aborted request (component unmount / react-query cancel) must
    // propagate immediately — never trigger the restart-recovery poll or a retry.
    if (init.signal?.aborted || (err as { name?: string } | null)?.name === 'AbortError') {
      throw err;
    }
    // A rotated PORT leaves the baked origin on a dead port → the fetch REJECTED
    // (no HTTP status, so the 401 path below can't see it). Re-read the republished
    // handoff and retry against the fresh origin. Only desktop (an injected secret)
    // can recover; on web the secret is undefined → rethrow byte-identically.
    if (staleSecret === undefined || !(await recoverRotatedLoopback(staleOrigin, staleSecret))) {
      throw err;
    }
    return fetch(apiUrl(path), { ...init, headers: withLoopbackSecret(init.headers) });
  }
  if (res.status === 401 && loopbackGlobals().__TIZOK_LOOPBACK_SECRET__ !== undefined) {
    if (await refreshLoopbackConfig()) {
      return fetch(apiUrl(path), { ...init, headers: withLoopbackSecret(init.headers) });
    }
  }
  return res;
}

/**
 * #111 auth-state coherence, root-cause (a): when the desktop window was
 * created BEFORE atlasd published its loopback handoff (fresh install
 * mid-onboarding, daemon restart), the `initialization_script` injected nothing
 * and the globals are absent — a relative fetch would resolve to
 * `tauri://localhost/api/*` (the webview's own asset protocol), where a sign-in
 * POST is a silent no-op. So on desktop (Tauri bridge present) with no injected
 * origin, re-read the handoff via `loopback_config` before the call — the SPA
 * adopts the proxy the moment the daemon is up, without recreating the window.
 * If the daemon still isn't reachable, fail fast with a network-shaped error
 * (no HTTP status), which `authErrors.classifyConnection` renders as the
 * legible "daemon starting" state — never a silent no-op.
 *
 * Web (no Tauri bridge): a strict no-op — requests stay byte-identical.
 */
async function adoptLoopbackConfig(): Promise<void> {
  if (loopbackGlobals().__TIZOK_API_ORIGIN__ !== undefined) return;
  if (!tauriInvoke()) return;
  await refreshLoopbackConfig();
  if (loopbackGlobals().__TIZOK_API_ORIGIN__ === undefined) {
    throw new TypeError(
      'Tizok agent (atlasd) is not reachable yet — its loopback handoff is not published',
    );
  }
}

interface LoopbackConfigDto {
  origin: string;
  secret: string;
}

interface TauriBridge {
  core?: { invoke?: (cmd: string, args?: unknown) => Promise<unknown> };
}

// The desktop webview exposes Tauri's command bridge at `window.__TAURI__`
// (tauri.conf `withGlobalTauri`); `undefined` on web.
function tauriInvoke(): ((cmd: string, args?: unknown) => Promise<unknown>) | undefined {
  if (typeof window === 'undefined') return undefined;
  return (window as unknown as { __TAURI__?: TauriBridge }).__TAURI__?.core?.invoke;
}

/**
 * Re-read the atlasd loopback handoff fresh (via the `loopback_config` command)
 * and write it back to the injected globals so every later call uses it too.
 * Returns true iff the origin/secret actually changed (atlasd rotated them) —
 * the caller retries only then. A no-op (false) on web / when atlasd is down.
 */
async function refreshLoopbackConfig(): Promise<boolean> {
  const invoke = tauriInvoke();
  if (!invoke) return false;
  let cfg: LoopbackConfigDto | null;
  try {
    cfg = (await invoke('loopback_config')) as LoopbackConfigDto | null;
  } catch {
    return false;
  }
  if (!cfg?.secret || !cfg.origin) return false;
  const g = loopbackGlobals();
  const changed =
    g.__TIZOK_LOOPBACK_SECRET__ !== cfg.secret || g.__TIZOK_API_ORIGIN__ !== cfg.origin;
  if (changed) {
    g.__TIZOK_API_ORIGIN__ = cfg.origin;
    g.__TIZOK_LOOPBACK_SECRET__ = cfg.secret;
  }
  return changed;
}

// Bounded poll for the republished handoff after an atlasd restart. ~6s total
// (20 × 300ms) comfortably covers a launchd stop→start→rebind→publish window;
// bounded so a genuinely-down daemon can't hang a request indefinitely.
const LOOPBACK_RESTART_RETRY_MAX = 20;
const LOOPBACK_RESTART_RETRY_MS = 300;

function delayMs(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Recover the loopback {origin, secret} after an atlasd restart rotated the PORT
 * (Wall-B). A rotated port leaves the baked origin on a DEAD port, so the fetch
 * REJECTS (connection refused) instead of returning a 401 — the 401 retry can't
 * see it. atlasd republishes the {port, secret} handoff when it comes back, but
 * `launchd::restart` (`desktop/src-tauri/src/ipc.rs`) returns BEFORE that, leaving
 * a brief down-window; so poll `loopback_config` (bounded) until the handoff
 * reports a config DIFFERENT from the one we just failed on, then the caller
 * retries once against it. Bounded so a truly-down daemon can't hang the request;
 * concurrency-safe — a peer `apiFetch` that already adopted the fresh handoff (so
 * the globals already moved off the stale pair) counts as recovered. `false` if
 * the daemon never republished within the budget (→ the request rejects, which
 * `authErrors.classifyConnection` renders as the transient "daemon starting").
 */
async function recoverRotatedLoopback(
  staleOrigin: string | undefined,
  staleSecret: string | undefined,
): Promise<boolean> {
  const rotated = () => {
    const g = loopbackGlobals();
    return g.__TIZOK_API_ORIGIN__ !== staleOrigin || g.__TIZOK_LOOPBACK_SECRET__ !== staleSecret;
  };
  for (let attempt = 0; attempt < LOOPBACK_RESTART_RETRY_MAX; attempt++) {
    if (rotated()) return true; // a concurrent apiFetch already adopted the new handoff
    await refreshLoopbackConfig();
    if (rotated()) return true;
    await delayMs(LOOPBACK_RESTART_RETRY_MS);
  }
  return rotated();
}

/**
 * Whether the SPA is running in the desktop webview. Keyed on EITHER the
 * injected loopback origin (set before boot when atlasd's handoff existed at
 * window creation) OR the Tauri command bridge — which is always present in the
 * desktop webview, including the #111 root-cause-(a) window that was created
 * before atlasd published its handoff (no injection yet). `false` on web. Used
 * to give the desktop a legible "daemon starting" state vs a web connectivity
 * error (see `authErrors.ts`, §5F).
 */
export function isDesktopRuntime(): boolean {
  return (
    loopbackGlobals().__TIZOK_API_ORIGIN__ !== undefined || tauriInvoke() !== undefined
  );
}

// ── Desktop Start-swarm → local agents (the org-builder F3 wire) ─────────────

/** What `project_start_swarm` returns (`desktop/src-tauri/src/ipc.rs ::
 *  OpenProjectOut`, flattened project + relaunch outcome — only the fields the
 *  console reads are typed here). */
export interface DesktopSwarmStart {
  /** The absolute local project folder the swarm was materialized into. */
  path: string;
  /** Did atlasd come back up pointed at the project? `false` + `relaunch_error`
   *  is a soft state: the project IS materialized; the daemon picks it up on its
   *  next start. */
  relaunched: boolean;
  relaunch_error?: string;
}

/**
 * After a successful `POST /api/mesh/{id}/start`, make the swarm REAL on this
 * machine: the shell writes `<project>/org.json` (the same saved wire doc) +
 * binds `space_id` into the project marker, persists the project as active, and
 * relaunches the installed atlasd pointed at it — which is what actually mints
 * the per-agent identities and spawns the agents. Desktop-only: resolves `null`
 * on web (no Tauri bridge), so callers invoke it unconditionally. Throws the
 * shell's error string when materialization fails — the caller surfaces that
 * honestly (the swarm's space exists server-side, but no local agents run).
 */
export async function desktopStartSwarmLocal(
  mesh: unknown,
  spaceId: string,
): Promise<DesktopSwarmStart | null> {
  const invoke = tauriInvoke();
  if (!invoke) return null; // web — the server-side start is all there is
  return (await invoke('project_start_swarm', { spaceId, mesh })) as DesktopSwarmStart;
}

export async function get<T>(path: string): Promise<T> {
  const res = await apiFetch(path, { credentials: 'include' });
  if (!res.ok) {
    throw {
      status: res.status,
      message: res.statusText,
      path,
    } satisfies ApiError;
  }
  return res.json() as Promise<T>;
}

async function postJson<T, B>(path: string, body: B): Promise<T> {
  const res = await apiFetch(path, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    // On a 403, lift the structured `{error:{code,message}}` envelope so a
    // caller can key on a machine-readable code — e.g. the invite gate's
    // `email_verification_required` (InviteModal shows a "verify to invite"
    // prompt), mirroring the notification-settings `sudo_required` path.
    // Additive: `code` is optional and ignored by callers that don't check it.
    const web = res.status === 403 ? await readWebError(res) : {};
    throw {
      status: res.status,
      code: web.code,
      message: res.statusText,
      path,
    } satisfies ApiError;
  }
  return res.json() as Promise<T>;
}

export function fetchMe(): Promise<CurrentUser> {
  return get<CurrentUser>('/api/me');
}

// Resend the email-verification link — `POST /api/resend-verification` (T-0087).
// Anti-enum: the backend returns a uniform 200 `{ok:true}` for any email; a 429
// means the 1/email/30min budget was hit (surface a calm "wait a moment", never
// an alarming error). Resolves on 200; throws `ApiError` (status carries the 429)
// otherwise so the caller can branch on rate-limiting.
export async function resendVerification(email: string): Promise<void> {
  const path = '/api/resend-verification';
  const res = await apiFetch(path, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email }),
  });
  if (!res.ok) {
    throw {
      status: res.status,
      message: await readErrorMessage(res),
      path,
    } satisfies ApiError;
  }
}

// Operator agent list — `GET /api/agents` (session-authed; the operator is the
// logged-in web user). Lists the operator's OWN agents without an admin biscuit
// — the admin-gated `/admin/agent` 401/405s a browser session (T-0076). Wire
// shape matches backend `ListAgentsResponse` + `AgentRow` in
// `src/presentation/admin/types.rs`.
export interface Agent {
  user_id: string;
  agent_suffix: string;
  display_name: string | null;
  created_at: string;
  expires_at?: string | null;
  paused?: boolean;
}

export interface ListAgentsResponse {
  agents: Agent[];
}

export function fetchAgents(): Promise<ListAgentsResponse> {
  return get<ListAgentsResponse>('/api/agents');
}

// Admin agent detail — `GET /admin/agent/{name}` per V1_INTERFACES §3.10.
export interface AgentDetail {
  user_id: string;
  agent_suffix: string;
  display_name: string | null;
  created_at: string;
  kind: string;
  room_id: string | null;
  last_activity_at: string | null;
}

export function fetchAgent(name: string): Promise<AgentDetail> {
  return get<AgentDetail>(`/admin/agent/${encodeURIComponent(name)}`);
}

// Self-service agent create — `POST /api/agents` (session-authed; the operator
// is the logged-in web user). Self-mode is implicit: a session user can only
// mint their OWN agents, so there is no `mode`/`for_email`. An optional `space`
// (room UUID, namespace URI, or slug) enrolls the new agent in that space
// atomically. Contrast `/admin/agent`, which needs an admin biscuit and a
// web-session onboarding user does not have one (401). See ConnectAgentModal.
export interface CreateAgentRequest {
  name: string;
  space?: string;
}

export interface CreateAgentResponse {
  user_id: string;
  namespace_uri: string;
  biscuit?: string;
  invite_token?: string;
  expires_at?: string;
  // One-line, copy-paste-ready shell command that exports the agent's token
  // (`export TIZOK_TOKEN='<biscuit>'`). Always present for this self-mode
  // endpoint. The UI offers it as a one-click COPY and never surfaces the raw
  // token. See ConnectAgentModal.
  connect_command?: string;
}

async function readErrorMessage(res: Response): Promise<string> {
  const text = await res.text().catch(() => '');
  return text.trim() || res.statusText;
}

// Parse the structured backend error envelope `{error:{code,message}}`
// (`WebErrorBody` in `src/presentation/web/types.rs`) off a non-ok response, so a
// gate like the 403 `sudo_required` step-up is machine-classifiable
// (`authErrors.isSudoRequired`) rather than leaking its raw prose. Best-effort:
// returns `{}` when the body isn't that shape (plain-text / other envelopes),
// leaving the caller's existing fallback message intact.
async function readWebError(
  res: Response,
): Promise<{ code?: string; message?: string }> {
  try {
    const body = (await res.json()) as {
      error?: { code?: unknown; message?: unknown };
    };
    const err = body.error;
    if (err && typeof err === 'object') {
      return {
        code: typeof err.code === 'string' ? err.code : undefined,
        message: typeof err.message === 'string' ? err.message : undefined,
      };
    }
  } catch {
    // Not the structured JSON envelope — fall through to `{}`.
  }
  return {};
}

export async function createAgent(
  body: CreateAgentRequest,
): Promise<CreateAgentResponse> {
  const path = '/api/agents';
  const res = await apiFetch(path, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    throw {
      status: res.status,
      message: await readErrorMessage(res),
      path,
    } satisfies ApiError;
  }
  return res.json() as Promise<CreateAgentResponse>;
}

// Per-agent credential actions — session-authed `POST /api/agents/{id}/…`
// (T-0094 Part B). `{id}` is the agent's `agent_suffix` (the URL-safe name the
// list response already carries), resolved server-side by
// `(parent_user_id = you, agent_suffix = id)` — never a cross-user id. Wire
// shapes mirror `AgentRevokeResponse` / `AgentRotateResponse` in
// `src/agent_auth/session_actions.rs`.

// `POST /api/agents/{id}/revoke` — kills the agent's biscuit immediately. The
// agent IDENTITY persists in the list (credential dead until a rotate re-issues
// a token). 404 = unknown/cross-owner (existence-hiding); 429 = rate-limited.
export interface RevokeAgentResponse {
  agent_user_id: string;
  revoked_at: string;
  /** Live MCP sessions torn down as part of the revoke (immediate disconnect). */
  revoked_mcp_sessions_count: number;
}

export async function revokeAgent(
  agentSuffix: string,
): Promise<RevokeAgentResponse> {
  const path = `/api/agents/${encodeURIComponent(agentSuffix)}/revoke`;
  const res = await apiFetch(path, { method: 'POST', credentials: 'include' });
  if (!res.ok) {
    throw {
      status: res.status,
      message: await readErrorMessage(res),
      path,
    } satisfies ApiError;
  }
  return res.json() as Promise<RevokeAgentResponse>;
}

// `POST /api/agents/{id}/rotate` — kills the old biscuit and mints a fresh one,
// returning a one-click `connect_command` (`export TIZOK_TOKEN='…'`), same shape
// as create. The raw token is a one-time secret — surface the command via COPY,
// never render it. 404 = unknown/cross-owner; 429 = rate-limited.
export interface RotateAgentResponse {
  agent_user_id: string;
  rotated_at: string;
  token: string;
  connect_command: string;
}

export async function rotateAgent(
  agentSuffix: string,
): Promise<RotateAgentResponse> {
  const path = `/api/agents/${encodeURIComponent(agentSuffix)}/rotate`;
  const res = await apiFetch(path, { method: 'POST', credentials: 'include' });
  if (!res.ok) {
    throw {
      status: res.status,
      message: await readErrorMessage(res),
      path,
    } satisfies ApiError;
  }
  return res.json() as Promise<RotateAgentResponse>;
}

export type ContactState = 'active' | 'blocked';
export type ContactStateFilter = ContactState | 'all';

export interface Contact {
  user_id: string;
  display_name: string | null;
  state: ContactState;
  connected_at: string | null;
  blocked_at: string | null;
  mutual_spaces: number;
  last_dm_at: string | null;
}

export interface ListContactsResponse {
  contacts: Contact[];
}

export function fetchContacts(
  state: ContactStateFilter = 'all',
  pattern?: string,
): Promise<ListContactsResponse> {
  const qs = new URLSearchParams({ state });
  if (pattern?.trim()) qs.set('pattern', pattern.trim());
  // Session-authed operator contacts list — `GET /api/contacts` (T-0076). The
  // admin-gated `/admin/contact` 401s a browser session.
  return get<ListContactsResponse>(`/api/contacts?${qs.toString()}`);
}

export interface SendContactInviteRequest {
  username: string;
}

export interface ContactInvite {
  id: string;
  direction: string;
  user_id: string;
  display_name: string | null;
  status: string;
  created_at: string;
  updated_at: string;
  acted_at: string | null;
}

export interface SendContactInviteResponse {
  invite: ContactInvite;
}

export async function sendContactInvite(
  body: SendContactInviteRequest,
): Promise<SendContactInviteResponse> {
  const path = '/admin/invite';
  const res = await apiFetch(path, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    throw {
      status: res.status,
      message: await readErrorMessage(res),
      path,
    } satisfies ApiError;
  }
  return res.json() as Promise<SendContactInviteResponse>;
}

export async function removeContact(username: string): Promise<void> {
  const path = `/admin/contact/${encodeURIComponent(username)}`;
  const res = await apiFetch(path, {
    method: 'DELETE',
    credentials: 'include',
  });
  if (!res.ok) {
    throw {
      status: res.status,
      message: await readErrorMessage(res),
      path,
    } satisfies ApiError;
  }
}

export async function blockContact(username: string): Promise<void> {
  const path = `/admin/contact/${encodeURIComponent(username)}/block`;
  const res = await apiFetch(path, {
    method: 'POST',
    credentials: 'include',
  });
  if (!res.ok) {
    throw {
      status: res.status,
      message: await readErrorMessage(res),
      path,
    } satisfies ApiError;
  }
}

export async function unblockContact(username: string): Promise<void> {
  const path = `/admin/contact/${encodeURIComponent(username)}/unblock`;
  const res = await apiFetch(path, {
    method: 'POST',
    credentials: 'include',
  });
  if (!res.ok) {
    throw {
      status: res.status,
      message: await readErrorMessage(res),
      path,
    } satisfies ApiError;
  }
}

// Free-form reviewer context cap, mirrored from the backend's
// REPORT_REASON_MAX_CHARS so the UI rejects before the round-trip.
export const REPORT_REASON_MAX_CHARS = 4096;

// One-way user safety report → `POST /admin/report` (#597). The server queues
// reviewer context and intentionally does not notify the reported user. Reason
// is optional free-form context.
export async function reportContact(
  username: string,
  reason?: string,
): Promise<void> {
  const path = '/admin/report';
  const res = await apiFetch(path, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ username, reason: reason || undefined }),
  });
  if (!res.ok) {
    throw {
      status: res.status,
      message: await readErrorMessage(res),
      path,
    } satisfies ApiError;
  }
}

// F.9 notification preferences — backend `/api/account/notifications`.
// Wire contract codified in `NOTIFICATIONS_BACKEND_WIRE.md` + locked by
// `src/lib/__tests__/notifications-wire.test.ts`.
export type NotificationCategory =
  | 'billing'
  | 'security'
  | 'room'
  | 'token'
  | 'digest';

export interface NotificationPreference {
  category: NotificationCategory;
  email_enabled: boolean;
  banner_enabled: boolean;
  webhook_enabled: boolean;
}

export interface NotificationPreferencesResponse {
  preferences: NotificationPreference[];
}

export interface UpdateNotificationPreferencesRequest {
  preferences: NotificationPreference[];
}

export function fetchNotificationPreferences(): Promise<NotificationPreferencesResponse> {
  return get<NotificationPreferencesResponse>('/api/account/notifications');
}

export async function updateNotificationPreferences(
  body: UpdateNotificationPreferencesRequest,
): Promise<NotificationPreferencesResponse> {
  const res = await apiFetch('/api/account/notifications', {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    // A 403 `sudo_required` carries `{error:{code,message}}`; lift the code so the
    // caller (via `authErrors.isSudoRequired`) opens the SudoModal + retries,
    // instead of rendering the raw "POST /api/sudo then retry" backend copy.
    const web = res.status === 403 ? await readWebError(res) : {};
    let message = res.statusText;
    if (res.status === 403 && web.code === 'sudo_required')
      message = 'Confirm your password to change notification settings.';
    else if (res.status === 401)
      message = 'Sudo elevation required. Sign in again to change notification settings.';
    else if (res.status === 400)
      message = "Webhook delivery isn't available yet (V1.5+).";
    throw {
      status: res.status,
      code: web.code,
      message,
      path: '/api/account/notifications',
    } satisfies ApiError;
  }
  return res.json() as Promise<NotificationPreferencesResponse>;
}

// `POST /api/sudo` — step-up re-authentication (F.3.5). Confirms the current
// operator's password to advance the session's 15-min sudo window, unblocking a
// sudo-gated action for a transparent retry. Cookie-session only; the backend
// argon2-verifies `{password}`. **204** No Content on success; **401** (uniform
// anti-enum) on a wrong password — the SudoModal surfaces that inline and keeps
// the user in place to retry. Never renders raw backend prose.
export async function elevateSudo(password: string): Promise<void> {
  const path = '/api/sudo';
  const res = await apiFetch(path, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ password }),
  });
  if (!res.ok) {
    throw {
      status: res.status,
      message:
        res.status === 401
          ? 'Incorrect password. Please try again.'
          : 'Could not re-authenticate right now. Please try again.',
      path,
    } satisfies ApiError;
  }
}

// `POST /api/account/delete` — F.7.2 soft-delete (sudo-gated; §1.11). Body carries
// the exact confirmation phrase + the F.7.4 active-rooms acknowledgement. A stale
// sudo returns **403 `sudo_required`** (lifted onto `code` for the SudoModal); a
// phrase mismatch returns **400**; **412** carries the active-rooms warning. On
// success **200** returns the soft-delete + scheduled-hard-delete timestamps.
export const ACCOUNT_DELETE_CONFIRMATION = 'I want to delete my account';

export interface AccountDeleteRequest {
  confirmation: string;
  acknowledged_active_rooms?: boolean;
}

export interface AccountDeleteResponse {
  soft_deleted_at: string;
  scheduled_hard_delete_at: string;
  rooms_archived_count: number;
}

export async function deleteAccount(
  body: AccountDeleteRequest,
): Promise<AccountDeleteResponse> {
  const path = '/api/account/delete';
  const res = await apiFetch(path, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const web = res.status === 403 ? await readWebError(res) : {};
    throw {
      status: res.status,
      code: web.code,
      message: web.message ?? res.statusText,
      path,
    } satisfies ApiError;
  }
  return res.json() as Promise<AccountDeleteResponse>;
}

export interface RoomMuteResponse {
  room_id: string;
  muted: boolean;
}

export async function updateRoomMutePreference(
  roomId: string,
  muted: boolean,
): Promise<RoomMuteResponse> {
  const path = `/api/spaces/${encodeURIComponent(roomId)}/mute`;
  const res = await apiFetch(path, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ muted }),
  });
  if (!res.ok) {
    throw {
      status: res.status,
      message: 'Could not update notification setting.',
      path,
    } satisfies ApiError;
  }
  return res.json() as Promise<RoomMuteResponse>;
}

export interface BillingInfo {
  plan_tier: 'free' | 'pro' | 'ultra';
  agent_count: number;
  has_subscription: boolean;
}

export function fetchBilling(): Promise<BillingInfo> {
  return get<BillingInfo>('/api/billing/me');
}

export interface BillingPortalResponse {
  portal_url: string;
}

export async function requestBillingPortal(): Promise<BillingPortalResponse> {
  const res = await apiFetch('/api/billing/portal', {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({}),
  });
  if (!res.ok) {
    throw {
      status: res.status,
      message: res.statusText,
      path: '/api/billing/portal',
    } satisfies ApiError;
  }
  return res.json() as Promise<BillingPortalResponse>;
}

export interface LoginRequest {
  email: string;
  password: string;
}

export interface LoginResponse {
  ok: boolean;
}

export async function login(body: LoginRequest): Promise<LoginResponse> {
  const res = await apiFetch('/api/login', {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    throw {
      status: res.status,
      message: res.status === 401 ? 'Invalid email or password' : res.statusText,
      path: '/api/login',
    } satisfies ApiError;
  }
  return res.json() as Promise<LoginResponse>;
}

export interface SignupRequest {
  email: string;
  password: string;
  display_name?: string;
}

export interface SignupResponse {
  verify_email_sent: boolean;
}

export interface VerifyEmailResponse {
  verified: boolean;
}

export interface PasswordResetRequestResponse {
  reset_email_sent: boolean;
}

export async function requestPasswordReset(email: string): Promise<PasswordResetRequestResponse> {
  const res = await apiFetch('/api/password-reset-request', {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email }),
  });
  if (!res.ok) {
    throw {
      status: res.status,
      message: res.statusText,
      path: '/api/password-reset-request',
    } satisfies ApiError;
  }
  return res.json() as Promise<PasswordResetRequestResponse>;
}

export interface PasswordResetRedeemRequest {
  token: string;
  new_password: string;
}

export interface PasswordResetRedeemResponse {
  reset: boolean;
}

export async function resetPassword(
  body: PasswordResetRedeemRequest,
): Promise<PasswordResetRedeemResponse> {
  const res = await apiFetch('/api/password-reset', {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    throw {
      status: res.status,
      message: res.statusText,
      path: '/api/password-reset',
    } satisfies ApiError;
  }
  return res.json() as Promise<PasswordResetRedeemResponse>;
}

export async function verifyEmail(token: string): Promise<VerifyEmailResponse> {
  const res = await apiFetch('/api/verify-email', {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ token }),
  });
  if (!res.ok) {
    throw {
      status: res.status,
      message: res.statusText,
      path: '/api/verify-email',
    } satisfies ApiError;
  }
  return res.json() as Promise<VerifyEmailResponse>;
}

export async function signup(body: SignupRequest): Promise<SignupResponse> {
  const res = await apiFetch('/api/signup', {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    let message = res.statusText;
    if (res.status === 409) message = 'An account already exists for this email.';
    else if (res.status === 422) message = 'Password must be at least 12 characters.';
    else if (res.status === 429) message = 'Too many signup attempts. Try again later.';
    throw {
      status: res.status,
      message,
      path: '/api/signup',
    } satisfies ApiError;
  }
  return res.json() as Promise<SignupResponse>;
}

export async function signOut(): Promise<void> {
  const res = await apiFetch('/api/logout', {
    method: 'POST',
    credentials: 'include',
  });
  if (!res.ok) {
    throw {
      status: res.status,
      message: res.statusText,
      path: '/api/logout',
    } satisfies ApiError;
  }
}

export function fetchMySpaces(): Promise<MemberSpace[]> {
  // Note: HTTP route is `/api/me/spaces` post-HYBRID-rename (PR #145 §1.20);
  // Rust types + JSON fields stay `room_id` per HYBRID directive.
  // Tolerance alias `/api/me/rooms` accepted V1.0.x; removed V1.1.
  return get<MemberSpace[]>('/api/me/spaces');
}

// Admin-namespace spaces list — Stream 2 `tizok space list` HTTP twin.
// Distinct from `fetchMySpaces` (member-of) — this is owned-namespace.
export function fetchSpaces(): Promise<ListSpacesResponse> {
  return get<ListSpacesResponse>('/api/spaces');
}

export interface CreateSpaceRequest {
  name: string;
  description?: string;
}

export function createSpace(body: CreateSpaceRequest): Promise<Space> {
  return postJson<Space, CreateSpaceRequest>('/api/spaces', body);
}

export interface UpdateSpaceRequest {
  name?: string;
  description?: string;
  invite?: string;
  kick?: string;
}

// W4 onboarding-demo: managed check-in cadence. Backend maps this to
// rooms.sleep_timeout_minutes while keeping MemberSpaceResponse untouched.
export interface Space {
  check_in_minutes: number | null;
}

export interface UpdateSpaceRequest {
  check_in_minutes?: number | null;
}

export async function updateSpace(
  spaceId: string,
  body: UpdateSpaceRequest,
): Promise<Space> {
  const path = `/api/spaces/${encodeURIComponent(spaceId)}`;
  const res = await apiFetch(path, {
    method: 'PATCH',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    throw {
      status: res.status,
      message: res.statusText,
      path,
    } satisfies ApiError;
  }
  return res.json() as Promise<Space>;
}

export async function deleteSpace(spaceId: string): Promise<void> {
  const path = `/api/spaces/${encodeURIComponent(spaceId)}`;
  const res = await apiFetch(path, {
    method: 'DELETE',
    credentials: 'include',
  });
  if (!res.ok) {
    throw {
      status: res.status,
      message: res.statusText,
      path,
    } satisfies ApiError;
  }
}

export function fetchSpace(roomId: string): Promise<MemberSpaceDetail> {
  return get<MemberSpaceDetail>(`/api/spaces/${encodeURIComponent(roomId)}`);
}

// Scheduled Requests (T-0155 backend / T-0156 composer) — a repeat rule on a
// Request. `repeat_rule` is the wire's only shape: `daily@HH:MM`, **UTC**. Nothing
// in the UI renders it; `lib/schedule.ts` converts at this edge in both directions.
export interface ScheduledRequest {
  schedule_id: string;
  space_id: string;
  /** URI of the person whose Request this is — the fire posts as them. */
  owner: string;
  repeat_rule: string;
  request_body: string;
  /** The operator's optional short title (T-0214). `null` when armed nameless — the
   *  surface then titles the Schedule by the first words of `request_body`
   *  (`scheduleName`). */
  name: string | null;
  next_fire_at: string;
  /** `null` until the first run. */
  last_fired_at: string | null;
  enabled: boolean;
  created_at: string;
}

export interface ListSchedulesResponse {
  schedules: ScheduledRequest[];
}

// What a Schedule actually DID (T-0157), read off the `schedule_fired` rung-0
// audit rows. This is the only source the amber past-tense confession may be
// built from — `lib/scheduleAliveness.ts` turns it into the sentence.
export interface ScheduleFire {
  fire_id: string;
  /** When the rule said to run. */
  scheduled_for: string;
  /** When it actually ran. */
  fired_at: string;
  /** Never negative. Past one sweep interval, the fire really was late. */
  late_by_seconds: number;
  outcome: 'posted' | 'deduped' | 'skipped_room_state';
  message_id: string | null;
  /** Did a machine take the turn this fire asked for? Tizok guarantees the FIRE,
   *  not the turn — `false` is the "waiting for a machine" sentence. */
  turn_taken: boolean;
}

/**
 * The subset of a fire the honest-state machine actually reads (`scheduleState`,
 * `fireConfession`). The fleet wire folds THIS onto each row (`FleetSchedule.last_fire`,
 * T-0214) so the fleet page can decide every row's state with no per-row `/fires`
 * fetch. A full {@link ScheduleFire} is assignable to it, so every existing caller keeps
 * working — the narrowing only widens what may be passed.
 */
export type FireSummary = Pick<
  ScheduleFire,
  'outcome' | 'turn_taken' | 'late_by_seconds' | 'scheduled_for' | 'fired_at'
>;

export interface ListScheduleFiresResponse {
  fires: ScheduleFire[];
}

export interface CreateScheduleRequest {
  request_body: string;
  repeat_rule: string;
  enabled?: boolean;
  /** Optional short title (T-0214). Omitted/blank ⇒ the row falls back to the first
   *  words of `request_body`. The server trims it and treats blank as absent. */
  name?: string;
}

export interface UpdateScheduleRequest {
  enabled?: boolean;
  repeat_rule?: string;
  request_body?: string;
}

function schedulesPath(spaceId: string): string {
  return `/api/spaces/${encodeURIComponent(spaceId)}/schedules`;
}

// The schedules routes reject with the structured `{error:{code,message}}` envelope
// and their messages are already written for a person ("request_body must not be
// empty — it is the Request the Schedule posts…"), so surface them rather than the
// bare statusText a generic helper would show.
export async function scheduleError(res: Response, path: string): Promise<ApiError> {
  const web = await readWebError(res);
  return {
    status: res.status,
    code: web.code,
    message: web.message ?? res.statusText,
    path,
  };
}

export async function fetchSchedules(spaceId: string): Promise<ListSchedulesResponse> {
  try {
    return await get<ListSchedulesResponse>(schedulesPath(spaceId));
  } catch (err) {
    // A deployment without the T-0155 routes answers 404 here. "No schedules
    // endpoint" means "this space has no Schedules", not a broken space sheet —
    // the same tolerance `fetchDMs` grants its pre-deploy backend.
    if ((err as ApiError).status === 404) return { schedules: [] };
    throw err;
  }
}

export async function createSchedule(
  spaceId: string,
  body: CreateScheduleRequest,
): Promise<ScheduledRequest> {
  const path = schedulesPath(spaceId);
  const res = await apiFetch(path, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw await scheduleError(res, path);
  return res.json() as Promise<ScheduledRequest>;
}

export async function updateSchedule(
  spaceId: string,
  scheduleId: string,
  body: UpdateScheduleRequest,
): Promise<ScheduledRequest> {
  const path = `${schedulesPath(spaceId)}/${encodeURIComponent(scheduleId)}`;
  const res = await apiFetch(path, {
    method: 'PATCH',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw await scheduleError(res, path);
  return res.json() as Promise<ScheduledRequest>;
}

export async function deleteSchedule(
  spaceId: string,
  scheduleId: string,
): Promise<void> {
  const path = `${schedulesPath(spaceId)}/${encodeURIComponent(scheduleId)}`;
  const res = await apiFetch(path, { method: 'DELETE', credentials: 'include' });
  if (!res.ok) throw await scheduleError(res, path);
}

/** One react-query key for a space's Schedules, shared by the composer (which arms
 *  them) and the space sheet (which lists them) so a new Schedule appears in the
 *  sheet without a reload. */
export function schedulesQueryKey(spaceId: string): [string, string, string] {
  return ['space', spaceId, 'schedules'];
}

/** Newest first. A Schedule's fire history outlives the Schedule itself, so this
 *  answers for a deleted one too — only the space gate can 404. */
export async function fetchScheduleFires(
  spaceId: string,
  scheduleId: string,
): Promise<ListScheduleFiresResponse> {
  const path = `${schedulesPath(spaceId)}/${encodeURIComponent(scheduleId)}/fires`;
  try {
    return await get<ListScheduleFiresResponse>(path);
  } catch (err) {
    // A deployment without this route answers 404. "No fire history" is a state
    // the honest-states layer already handles (it just doesn't confess), so it
    // must not break the row — the same tolerance `fetchSchedules` grants.
    if ((err as ApiError).status === 404) return { fires: [] };
    throw err;
  }
}

export function scheduleFiresQueryKey(
  spaceId: string,
  scheduleId: string,
): [string, string, string, string, string] {
  return ['space', spaceId, 'schedules', scheduleId, 'fires'];
}

export function fetchMessages(
  roomId: string,
  cursor?: string,
): Promise<MessagePage> {
  const qs = cursor ? `?cursor=${encodeURIComponent(cursor)}` : '';
  return get<MessagePage>(
    `/api/spaces/${encodeURIComponent(roomId)}/messages${qs}`,
  );
}

export interface SendMessageRequest {
  content: string;
  parent_id?: string;
  addressed_to?: string;
}

export function sendMessage(
  roomId: string,
  body: SendMessageRequest,
): Promise<Message> {
  return postJson<Message, SendMessageRequest>(
    `/api/spaces/${encodeURIComponent(roomId)}/messages`,
    body,
  );
}

export interface MintInviteRequest {
  trust_level: 'self_device' | 'cross_human_persistent' | 'cross_human_guest';
  intent: string;
  custom_name?: string;
}

export interface MintedInvite {
  invite_url: string;
  invite_token: string;
  persona: string;
  /** `null` for a persistent invite (no crypto/room expiry) — the UI omits the
   *  "Expires …" line rather than rendering an epoch. */
  expires_at: string | null;
}

// Mint a trust-scoped collaboration invite — the prominent "Invite" modal.
// Session-authed `POST /api/invites` (the mint creates a NEW collaboration, so
// the endpoint is NOT room-scoped). Returns the shareable `/join?token=` link.
export function mintInvite(body: MintInviteRequest): Promise<MintedInvite> {
  return postJson<MintedInvite, MintInviteRequest>('/api/invites', body);
}

export const MAX_UPLOAD_BYTES = 50 * 1024 * 1024;

// Poll interval for an *open* room's live data (its message thread + detail), so
// new messages/members appear without a manual refresh. MessageThread has no
// websocket/subscription — it fetches on mount — so a light poll is what makes
// the conversation update live. 2.5s balances liveness against request volume;
// only the focused room polls (the sidebar list does not).
export const OPEN_ROOM_REFETCH_MS = 2500;

export interface UploadProgress {
  loaded: number;
  total: number;
}

export interface UploadResult {
  url: string;
  name: string;
}

// Uploads via the existing `POST /upload/room/{room_id}` multipart endpoint
// (server streams to S3 + auto-posts a system message). XHR is required for
// progress events; fetch() does not expose upload-side progress yet.
//
// See docs/TECH_DEBT.md V1.5+ "Unify HTTP /upload/room and MCP request_upload"
// for the divergence-with-MCP-tool context.
export function uploadFile(
  roomId: string,
  file: File,
  onProgress?: (p: UploadProgress) => void,
  signal?: AbortSignal,
): Promise<UploadResult> {
  return new Promise<UploadResult>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    const form = new FormData();
    form.append('file', file, file.name);

    xhr.open('POST', apiUrl(`/upload/room/${encodeURIComponent(roomId)}`));
    xhr.withCredentials = true;
    // Desktop: the loopback proxy gates on the secret header; XHR can't use
    // apiFetch, so set it directly (after open()). No-op on web (no secret).
    const uploadSecret = loopbackGlobals().__TIZOK_LOOPBACK_SECRET__;
    if (uploadSecret) xhr.setRequestHeader(LOOPBACK_SECRET_HEADER, uploadSecret);

    if (onProgress) {
      xhr.upload.addEventListener('progress', (e) => {
        if (e.lengthComputable) {
          onProgress({ loaded: e.loaded, total: e.total });
        }
      });
    }

    xhr.addEventListener('load', () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve({ url: extractUrl(xhr.responseText, file.name), name: file.name });
      } else {
        reject({
          status: xhr.status,
          message: xhr.statusText,
          path: `/upload/room/${roomId}`,
        } satisfies ApiError);
      }
    });
    xhr.addEventListener('error', () =>
      reject({ status: 0, message: 'network error', path: `/upload/room/${roomId}` } satisfies ApiError),
    );
    xhr.addEventListener('abort', () =>
      reject({ status: 0, message: 'aborted', path: `/upload/room/${roomId}` } satisfies ApiError),
    );

    if (signal) {
      if (signal.aborted) {
        xhr.abort();
        return;
      }
      signal.addEventListener('abort', () => xhr.abort());
    }

    xhr.send(form);
  });
}

// Server returns plain text like "File uploaded successfully to <url>".
// Extract the URL fragment if present; otherwise synthesize a relative path.
function extractUrl(responseText: string, name: string): string {
  const match = /https?:\/\/\S+/.exec(responseText);
  return match ? match[0] : `/uploads/${encodeURIComponent(name)}`;
}

export interface FileMessagePayload {
  type: 'file';
  url: string;
  name: string;
}

export function parseFileMessage(content: string): FileMessagePayload | null {
  if (!content.startsWith('{')) return null;
  try {
    const parsed = JSON.parse(content) as Partial<FileMessagePayload>;
    if (parsed.type === 'file' && typeof parsed.url === 'string' && typeof parsed.name === 'string') {
      return parsed as FileMessagePayload;
    }
    return null;
  } catch {
    return null;
  }
}

export function detectMediaKind(name: string): 'image' | 'video' | 'other' {
  const ext = name.toLowerCase().split('.').pop() ?? '';
  if (['png', 'jpg', 'jpeg', 'gif', 'webp', 'avif', 'svg'].includes(ext)) return 'image';
  if (['mp4', 'webm', 'mov', 'm4v'].includes(ext)) return 'video';
  return 'other';
}

// Invite list + actions — wired to the invite cluster endpoints (PR #684).
export interface Invite {
  username: string;
  display_name: string | null;
  direction: 'incoming' | 'outgoing';
  sent_at: string;
  status: 'pending' | 'accepted' | 'rejected' | 'cancelled';
}

export interface ListInvitesResponse {
  invites: Invite[];
}

export function fetchInvites(
  direction?: 'incoming' | 'outgoing',
): Promise<ListInvitesResponse> {
  const qs = direction ? `?direction=${direction}` : '';
  // Session-authed operator invites list — `GET /api/invites` (T-0076). The
  // backend maps its canonical `ContactInviteRow` to this `Invite` shape
  // (`username`/`sent_at`); the admin-gated `/admin/invites` path 404s a
  // browser session.
  return get<ListInvitesResponse>(`/api/invites${qs}`);
}

async function postEmpty(path: string): Promise<void> {
  const res = await apiFetch(path, {
    method: 'POST',
    credentials: 'include',
  });
  if (!res.ok) {
    throw {
      status: res.status,
      message: await readErrorMessage(res),
      path,
    } satisfies ApiError;
  }
}

export function sendInvite(username: string): Promise<void> {
  return postJson<void, { username: string }>('/admin/invite/send', {
    username,
  }) as Promise<void>;
}

export function acceptInvite(username: string): Promise<void> {
  return postEmpty(`/admin/invite/${encodeURIComponent(username)}/accept`);
}

export function rejectInvite(username: string): Promise<void> {
  return postEmpty(`/admin/invite/${encodeURIComponent(username)}/reject`);
}

export function cancelInvite(username: string): Promise<void> {
  return postEmpty(`/admin/invite/${encodeURIComponent(username)}/cancel`);
}

// DM conversations — pre-wire scaffold for the DM backend cluster (PRs #637,
// #638). The component gracefully degrades to empty-state when the endpoint
// returns 404 (not yet deployed), so this wires up with a one-line swap.
export interface DMConversation {
  dm_id: string;
  peer_username: string;
  peer_display_name: string | null;
  last_message_at: string | null;
  unread_count: number;
}

export interface ListDMsResponse {
  dms: DMConversation[];
}

export async function fetchDMs(): Promise<ListDMsResponse> {
  try {
    return await get<ListDMsResponse>('/api/me/dms');
  } catch (err) {
    // A deployment without the DM cluster answers 404 here (staging today).
    // A missing endpoint means "no DMs", not an error: every consumer shares
    // the ['dms'] query, and `useFirstRun` gates the WHOLE shell on it
    // settling successfully — surfacing the 404 as an error wedged the
    // console on "Loading…" forever after onboarding. Other failures (500,
    // network) still throw; callers keep their own error handling.
    if ((err as ApiError).status === 404) {
      return { dms: [] };
    }
    throw err;
  }
}

// operator↔agent DM — resolve-or-mint (Stage 2, "talk to your agents"). POST /api/dm
// LAZY-MINTS the 2-member DM room the FIRST time the operator opens a conversation with
// an agent, and returns the same `dm_id` on every subsequent call (idempotent). The
// returned `dm_id` feeds straight into the EXISTING DM surfaces the Chat tab reuses:
// GET /api/me/dms (the header's friendly name) + GET/POST /api/spaces/{dm_id}/messages
// (the thread + composer). `with` is the OTHER party — the agent's member URI (or a bare
// name; the backend normalizes `cea` / `@cea` / `@cea:tizok.ai` identically).
export interface ResolveDmResponse {
  dm_id: string;
  peer_username: string;
  peer_display_name: string | null;
  created: boolean;
}

export function resolveDm(target: string): Promise<ResolveDmResponse> {
  return postJson<ResolveDmResponse, { with: string }>('/api/dm', { with: target });
}
